/**
 * PlaceInfoWindow - Google Maps InfoWindow 地點詳情
 * 保留照片，文字格式對齊 Activity Detail 設計
 */

import { InfoWindow } from '@vis.gl/react-google-maps';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { getPlacePhotoUrl } from '../../utils/photoUrl';
import type { Activity } from '../../types/activity';
import { getActivityTypeConfig } from '../../utils/activityTypes';


interface PlaceInfoWindowProps {
  activity: Activity;
  onClose: () => void;
  tripId?: string;
  /** 對應 marker 的實際 DOM 實例，讓 InfoWindow 依 marker 大小自動算偏移量，避免卡片蓋住圖標 */
  anchor?: google.maps.marker.AdvancedMarkerElement | null;
  /** 沒有登入權限可導到行程詳情頁時（例如分享頁），改用這個開啟唯讀詳情 */
  onViewDetails?: (activity: Activity) => void;
}

export default function PlaceInfoWindow({ activity, onClose, tripId, anchor, onViewDetails }: PlaceInfoWindowProps) {
  const navigate = useNavigate();
  const photoUrl = getPlacePhotoUrl(activity.placeId, 400);

  if (!activity.lat || !activity.lng) return null;

  const typeLabel = getActivityTypeConfig(activity.type).label;
  const subtitle = [typeLabel, activity.location].filter(Boolean).join(' · ');

  return (
    <InfoWindow
      {...(anchor ? { anchor } : { position: { lat: activity.lat, lng: activity.lng } })}
      onCloseClick={onClose}
      headerDisabled
    >
      <div className="relative w-60 overflow-hidden rounded-xl bg-white">
        {/* 關閉按鈕：headerDisabled 拿掉了內建的關閉鈕，這裡補一個，避免卡片蓋住 marker 後點不掉 */}
        <button
          onClick={onClose}
          aria-label="關閉"
          className="absolute top-2 right-2 z-10 flex size-6 items-center justify-center rounded-full bg-white/90 text-slate-600 shadow hover:bg-white transition-colors"
        >
          <X size={14} />
        </button>

        {/* 照片區域 */}
        {photoUrl ? (
          <img
            src={photoUrl}
            alt={activity.name}
            className="w-full h-32 object-cover"
          />
        ) : (
          <div className="h-32 bg-slate-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
        )}

        {/* 文字區域 */}
        <div className="px-3 pt-2.5 pb-3 flex flex-col gap-2">
          <div>
            <h3 className="text-slate-900 text-sm font-bold leading-tight">{activity.name}</h3>
            <p className="text-slate-400 text-xs mt-0.5">{subtitle}</p>
          </div>

          {/* 查看詳情按鈕 */}
          {(tripId || onViewDetails) && (
            <button
              onClick={() => (onViewDetails ? onViewDetails(activity) : navigate(`/trips/${tripId}/itinerary/${activity.id}`))}
              className="w-full rounded-lg bg-[#0dccf2] py-1.5 text-xs font-bold text-white hover:bg-opacity-90 transition-colors"
            >
              查看詳情
            </button>
          )}
        </div>
      </div>
    </InfoWindow>
  );
}
